import Head from "next/head";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { RTL } from "src/components/rtl";
import { SplashScreen } from "src/components/splash-screen";
import { Toaster } from "src/components/toaster";
import {
  SettingsConsumer,
  SettingsProvider,
} from "src/contexts/settings-context";
import { AuthConsumer, AuthProvider } from "src/contexts/auth/jwt-context";
import { useNprogress } from "src/hooks/use-nprogress";
import { createTheme } from "src/theme";
import { createEmotionCache } from "src/utils/create-emotion-cache";
import { AxiosInterceptor } from "src/utils//axios-interceptor";

import "simplebar-react/dist/simplebar.min.css";
import "src/locales/i18n";
import "react-quill/dist/quill.snow.css";

import "src/styles/apexcharts-custom.css";
import "src/styles/map.css";

import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import ZoomProvider from "src/contexts/zoom-context";
import ErrorBoundary from "src/components/error-boundary";

const clientSideEmotionCache = createEmotionCache();

const App = (props) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const { t } = useTranslation();
  useNprogress();

  const getLayout = Component.getLayout ?? ((page) => page);

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>{t(tokens.label.systemShortName)}</title>
        <meta
          name="viewport"
          content="initial-scale=1, width=device-width"
        />
      </Head>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AuthProvider>
          <AuthConsumer>
            {(auth) => (
              <AxiosInterceptor>
                <ZoomProvider>
                  <SettingsProvider>
                    <SettingsConsumer>
                      {(settings) => {
                        // Prevent theme flicker when restoring custom settings from browser storage
                        if (!settings.isInitialized) {
                          //return null;
                        }

                        const pagePaletteMode =
                          Component.paletteMode || settings.paletteMode;

                        const theme = createTheme({
                          colorPreset: settings.colorPreset,
                          contrast: settings.contrast,
                          direction: settings.direction,
                          paletteMode: pagePaletteMode,
                          responsiveFontSizes: settings.responsiveFontSizes,
                        });

                        // Prevent guards from redirecting
                        const showSlashScreen = !auth.isInitialized;

                        return (
                          <ThemeProvider theme={theme}>
                            <ErrorBoundary>
                              <Head>
                                <meta
                                  name="color-scheme"
                                  content={pagePaletteMode}
                                />
                                <meta
                                  name="theme-color"
                                  content={theme.palette.neutral[900]}
                                />
                              </Head>
                              <RTL direction={settings.direction}>
                                <CssBaseline />
                                {showSlashScreen ? (
                                  <SplashScreen />
                                ) : (
                                  <>{getLayout(<Component {...pageProps} />)}</>
                                )}
                                <Toaster />
                              </RTL>
                            </ErrorBoundary>
                          </ThemeProvider>
                        );
                      }}
                    </SettingsConsumer>
                  </SettingsProvider>
                </ZoomProvider>
              </AxiosInterceptor>
            )}
          </AuthConsumer>
        </AuthProvider>
      </LocalizationProvider>
    </CacheProvider>
  );
};

export default App;
